/**
 * Offline FAQ copy bundled with the browser bundle.
 *
 * The Help Center's FAQs tab is served by `GET /help/v1/faqs`; these entries are
 * what it falls back to when that call fails, so the overlay still answers the
 * obvious questions at kickoff when the sideline connection is bad. They are a
 * deliberately small subset of the shipped seed in
 * `services/help/src/seeds/faq-entries.ts` — keep the wording in step.
 */
import type { FaqEntry } from './api-types';

/** Offline copy of the most-asked entries, in the wire shape of `FaqEntry`. */
export const FALLBACK_FAQS: readonly FaqEntry[] = [
  {
    id: 'fallback-team-code',
    question: 'What is my team code?',
    answer_markdown:
      'The team code is the short slug in your portal URL — for example "westview-eagles" in /app/westview-eagles. Your Team Admin chose it at signup and it never changes.',
    tags: ['signup'],
    persona_tags: [],
    sort_order: 10,
    scope: 'platform',
  },
  {
    id: 'fallback-mfa',
    question: 'Why am I asked for a 6-digit code?',
    answer_markdown:
      'Admin and coaching personas require multi-factor authentication. Enrol an authenticator app once; after that you enter the current code each time you sign in on a new session. Lost the authenticator? Enter one of the backup codes you saved at setup, or ask your Team Admin for account recovery — authenticators can only be replaced from Settings → Preferences after signing in, never from the sign-in screen.',
    tags: ['signup', 'security'],
    persona_tags: [],
    sort_order: 20,
    scope: 'platform',
  },
  {
    id: 'fallback-persona-landing',
    question: 'Why does my colleague see a different first page?',
    answer_markdown:
      'Each persona lands on the view it needs: coordinators on Coaches Highlight for their side of the ball, coaches on Game Summary, stat operators on Play Entry, Team Admins on the portal dashboard.',
    tags: ['rbac'],
    persona_tags: [],
    sort_order: 30,
    scope: 'platform',
  },
  {
    id: 'fallback-archive-row-colours',
    question: 'Why are some play rows coloured in the Play Archive?',
    answer_markdown:
      'Each row is tinted by its Success measure: green means the play was a success, red means it was not, and no tint (neutral) means the play could not be measured. Every run or pass with a down and a gain/loss is measured — a 3rd/4th down without the spot uses your late-down fallback yardage (10 by default). Only snaps like play type Other, a punt with no kick distance or a field goal with no made/missed tag stay neutral; hover the dash in the Success column for the reason. Tap the Success header to filter Success, Not success or Not measured.',
    tags: ['archive', 'success-measure'],
    persona_tags: [],
    sort_order: 45,
    scope: 'platform',
  },
  {
    id: 'fallback-trial',
    question: 'What happens when the 30-day trial ends?',
    answer_markdown:
      'Nothing is charged — no card is collected for the trial. On day 30 the account moves to the Free tier automatically; your data stays, and a Team Admin can pick a paid plan at any time.',
    tags: ['trial', 'billing'],
    persona_tags: [],
    sort_order: 50,
    scope: 'platform',
  },
  {
    id: 'fallback-ferpa',
    question: 'What may we not put into Gridiron Tracker?',
    answer_markdown:
      'Never enter FERPA-protected education records: date of birth, contact or guardian information, medical notes, photos, disciplinary records or academic records. Football program data only.',
    tags: ['privacy', 'ferpa'],
    persona_tags: [],
    sort_order: 60,
    scope: 'platform',
  },
  {
    id: 'fallback-export',
    question: 'How do I export data from a page?',
    answer_markdown:
      'Click **Export** at the top right of the page (Play Archive, Game Summary, Stat Tracker, Playbook, Roster, Events, Team Users or Fanatics). The file holds what the page is showing — columns, filters and sort, custom stats included. Tick, rename and reorder columns under **Custom**, save the layout as a template to reuse it on any page, pick Excel or CSV and click **Download**.',
    tags: ['export'],
    persona_tags: [],
    sort_order: 280,
    scope: 'platform',
  },
  {
    id: 'fallback-star-play',
    question: 'How do I star a play for later review?',
    answer_markdown:
      'Click the ☆ in the first column of the Play Archive or **Star** in the Play Entry Actions card. Stars are shared with the whole staff and show up live on the Coaches Highlight **Starred plays** card — the Offense board lists starred offense plays and the Defense board starred defense plays, each row with #, unit, situation, result and the concept (plus the formation where it was logged).',
    tags: ['archive', 'play-entry'],
    persona_tags: [],
    sort_order: 283,
    scope: 'platform',
  },
];
